import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'

import Layout from '../components/Layout'
import CustomHero from '../components/Heros/CustomHero'
import CustomHeroV3 from '../components/Heros/CustomHeroV3'
import CustomHeroCarousel from '../components/Heros/CustomHeroCarousel'

import BREAKPOINTS from '../theme/breakPoint'
import CompanyCard from '../components/Cards/CompanyCard'
import BlockCard from '../components/Cards/BlockCard'
import BlockCardV3 from '../components/Cards/BlockCardV3'
import SchumerBox from '../components/SchumerBox'
import InfoCard from '../components/Cards/InfoCard'
import PeopleChoose from '../components/PeopleChoose'
import RatesTermsRibbon from '../components/RatesTermsRibbon'
import Carousel from '../components/CarouselUtilities/Carousel'
import TrustPilot from '../components/Utilities/TrustPilot'
import TrustPilotV3 from '../components/Utilities/TrustPilotV3/TrustPilotV3'
import Columns from '../components/Columns'
import RatesTerms from '../components/RatesTerms'
import RatesTermsV3 from '../components/RatesTermsV3'
import CustomersHelped from '../components/CustomersHelped'
import LoanCardV2 from '../components/Cards/LoanCardV2'
import FeatureV2 from '../components/FeatureV2'
import ApplyParamsContext from '../context/ApplyParamsContext'
import Headline from '../components/Headline/Headline'
import PartnerVisit from '../components/PartnerVisit'
import PressCardSet from '../components/Cards/PressCardsSet'
import PressCardSetV3 from '../components/Cards/PressCardsSetV3'
import RichTextBlock from '../components/RichTextBlock'
import ContentfulGrid from '../components/Grid/ContentfulGrid'
import FDICDisclosure from '../components/FDICDisclosure'
import BlockCardGrid from '../components/Cards/BlockCardGrid'
import BlogPost from '../components/BlogPost'
import DecorationV3 from '../components/Decoration/DecorationV3'
import DifferenceTable from '../components/DifferenceTable'
import GraphicComposition from '../components/GraphicComposition'
import Stats from '../components/Stats'
import HardCoded from '../components/HardCoded'
import Timestamp from '../components/Timestamp'
import AccordionGrid from '../components/AccordionGrid'
import Pagination from '../components/pagination'
import { saveAttributionParams } from '../helpers'
import { getEnvVar } from '../helpers/environment'

class CustomLanding extends Component {
  constructor(props) {
    super(props)

    this.state = {
      windowWidth: 0,
      cardMargin: 0,
      changeLayout: false,
      toggleCarousels: false
    }
    this.cardDetails = React.createRef()
    this.dropInHeader = React.createRef()
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()

    if (!this.cardDetails.current) {
      this.setState({ cardMargin: 0 })
      return
    }

    const marginLeft = window
      .getComputedStyle(this.cardDetails.current)
      .getPropertyValue('margin-left')

    const marginRight = window
      .getComputedStyle(this.cardDetails.current)
      .getPropertyValue('margin-right')

    const cardMargin = parseInt(marginLeft, 10) + parseInt(marginRight, 10)

    this.setState({ cardMargin })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    const windowWidth = window.innerWidth

    this.setState({
      windowWidth,
      changeLayout: windowWidth >= BREAKPOINTS.MEDIUM,
      toggleCarousels: windowWidth >= BREAKPOINTS.LARGE
    })
  }

  render() {
    const {
      pageContext: { timestamp, blogCategory },
      data: {
        contentfulSeoMetadata,
        contentfulLandingPageGod: {
          pageTitle,
          seoReference,
          pricingStrategy,
          slug,
          extraApplyParams,
          hideNavigation,
          navigation,
          hero,
          rows,
          legalStatements,
          v3Styles,
          disclosures,
          footer
        }
      }
    } = this.props
    const { changeLayout } = this.state

    if (typeof window !== 'undefined' && contentfulSeoMetadata?.dynamicApplyParams) {
      saveAttributionParams(contentfulSeoMetadata.dynamicApplyParams)
    }

    // TODO: PROS-377 add useMemo and make sure the component is rerender when is needed
    // eslint-disable-next-line
    const applyParams = {
      dynamicApplyParams: contentfulSeoMetadata?.dynamicApplyParams,
      extraApplyParams: extraApplyParams ? JSON.parse(extraApplyParams.internal.content) : {}
    }

    if (pricingStrategy && pricingStrategy !== 'None') {
      const strategy = pricingStrategy.split(' ')

      if (typeof window !== 'undefined') {
        if (window.location.href.indexOf('/credit-card/landing/') >= 0) {
          if (strategy.length > 7) {
            applyParams.extraApplyParams.strategy = strategy[strategy.length - 1]
          }
        }
      }
    }

    const {
      canonicalUrl,
      seoRobots,
      seoDescription,
      pageTitle: seoPageTitle,
      openGraphImage,
      openGraphTitle,
      openGraphDescription
    } = seoReference || {}
    const openGraph = {
      title: openGraphTitle,
      description: openGraphDescription?.openGraphDescription,
      image: openGraphImage
    }
    const metaRobots = seoRobots
    const metaDescription = seoDescription?.seoDescription
    const metaDataCustomPageTitle = seoPageTitle || pageTitle || contentfulSeoMetadata?.pageTitle

    const keyIds = new Map()
    const displayTimestamp = getEnvVar('APPLICATION_ENV') !== 'production'

    return (
      <ApplyParamsContext.Provider value={applyParams}>
        <Layout
          dropInHeader={this.dropInHeader}
          hasFullLegal
          primary
          metaData={undefined}
          metaDataCustom={{
            canonicalUrl,
            pageTitle: metaDataCustomPageTitle,
            metaDescription,
            openGraph,
            metaRobots
          }}
          slug={slug}
          pageCategory="landing"
          footerLegalType="custom"
          noNavigation={hideNavigation || null}
          navigation={navigation}
          heros={hero}
          footerLegalData={legalStatements}
          v3Styles={v3Styles}
          disclosuresData={disclosures}
          footerData={footer}
          blogCategory={blogCategory}
        >
          {(() => {
            if (hero?.length) {
              if (hero.length > 1) return <CustomHeroCarousel data={hero} slug={slug} />
              return v3Styles ? (
                <CustomHeroV3 data={hero[0]} slug={slug} />
              ) : (
                <CustomHero data={hero[0]} slug={slug} />
              )
            }
            return null
          })()}

          <main ref={this.dropInHeader} id="maincontent">
            {(rows || []).map(row => {
              const { id } = row
              if (keyIds.has(id)) {
                keyIds.set(id, keyIds.get(id) + 1)
              } else {
                keyIds.set(id, 0)
              }

              // make sure that no two keys are the same
              const key = keyIds.get(id) === 0 ? id : `${id}-${keyIds.get(id)}`

              if (!row.sys) return null
              const contentTypeId = row.sys.contentType.sys.id

              if (contentTypeId === 'chooseUsSet') {
                if (v3Styles) return null
                return <PeopleChoose key={key} data={row} contentfulEntryTitle={row.entryTitle} />
              }
              if (contentTypeId === 'carousel') {
                const { windowWidth, cardMargin, toggleCarousels } = this.state
                let cardWidth = changeLayout ? 379 : 317
                if (toggleCarousels) cardWidth = '100%'

                let isLoanCard = false
                let cardList = row.carouselItems
                cardList = cardList.map(el => {
                  const cardKey = JSON.stringify(el)
                  /* eslint no-param-reassign: "error" */
                  if (el.sys.contentType.sys.id === 'carouselItem') {
                    el.position = el.position.replace(/\\u2011/g, '\u2011')
                    el.position = el.position.replace(/\\u00A0/g, '\u00A0')
                    return <CompanyCard data={el} key={cardKey} />
                  }

                  if (el.sys.contentType.sys.id === 'blockCard') {
                    isLoanCard = true
                    return <LoanCardV2 data={el} key={cardKey} />
                  }

                  return null
                })
                if (isLoanCard && toggleCarousels) {
                  return (
                    <section
                      key={key}
                      className={`carousel card ${row.backgroundColor === 'Gray' ? 'gray' : ''}`}
                    >
                      <div className="loan-card__wrapper">{cardList}</div>
                    </section>
                  )
                }
                cardList.unshift(cardList.pop())
                return (
                  <Carousel
                    key={this.id}
                    windowWidth={windowWidth}
                    cardWidth={cardWidth}
                    cardMargin={cardMargin}
                    cardDetails={this.cardDetails}
                    secondary={toggleCarousels}
                    isGray={row.backgroundColor === 'Gray'}
                  >
                    {cardList}
                  </Carousel>
                )
              }

              if (contentTypeId === 'ratesAndTermsRibbon') {
                return <RatesTermsRibbon key={key} data={row} />
              }

              if (contentTypeId === 'schumerBox') {
                return <SchumerBox key={key} data={row} />
              }

              if (contentTypeId === 'trustPilot') {
                return v3Styles ? (
                  <TrustPilotV3 key={key} data={row} />
                ) : (
                  <TrustPilot key={key} data={row} />
                )
              }

              if (contentTypeId === 'ratesTermsStatesDropdown') {
                return v3Styles ? (
                  <RatesTermsV3
                    key={row.id}
                    changeLayout={changeLayout}
                    data={row.ratesTermsData.ratesAndTermsData.states}
                    type={row.isSecuredLoans ? 'securedPersonalLoan' : 'personalLoan'}
                  />
                ) : (
                  <RatesTerms
                    key={key}
                    changeLayout={changeLayout}
                    data={row.ratesTermsData.ratesAndTermsData.states}
                    type={row.isSecuredLoans ? 'securedPersonalLoan' : 'personalLoan'}
                  />
                )
              }

              if (contentTypeId === 'customersHelped') {
                return (
                  <CustomersHelped
                    key={key}
                    data={row}
                    customersHelped={contentfulSeoMetadata.customersHelped}
                  />
                )
              }

              if (contentTypeId === 'blockCard') {
                return v3Styles ? (
                  <BlockCardV3 key={key} data={row} />
                ) : (
                  <BlockCard key={key} data={row} />
                )
              }

              if (contentTypeId === 'infoCard') {
                return <InfoCard key={key} data={row} />
              }

              if (contentTypeId === 'imageSet') {
                return <FeatureV2 key={key} data={row} />
              }

              if (contentTypeId === 'headline') {
                return <Headline key={key} data={row} />
              }

              if (contentTypeId === 'partnerVisitThankYou') {
                return <PartnerVisit key={key} data={row} />
              }

              if (contentTypeId === 'pressLinksSet') {
                return v3Styles ? (
                  <PressCardSetV3 key={key} data={row} />
                ) : (
                  <PressCardSet key={key} data={row} />
                )
              }

              if (contentTypeId === 'columns') {
                return (
                  <Columns
                    key={key}
                    data={row}
                    customersHelped={contentfulSeoMetadata.customersHelped}
                  />
                )
              }
              if (contentTypeId === 'columnsV3') {
                return <ContentfulGrid key={row.id} data={row} />
              }

              if (contentTypeId === 'textBlock') {
                return <RichTextBlock key={key} data={row} typeOfContent="legal" />
              }

              if (contentTypeId === 'memberFdicBlock') {
                return <FDICDisclosure key={key} data={row} />
              }

              if (contentTypeId === 'testingBlogPost') {
                return <BlogPost key={key} data={row} />
              }

              if (contentTypeId === 'blockCardGrid') {
                return <BlockCardGrid key={key} data={row} />
              }

              if (contentTypeId === 'decorationV3') {
                return <DecorationV3 key={key} data={row} />
              }

              if (contentTypeId === 'differenceTable') {
                return <DifferenceTable key={key} data={row} />
              }

              if (contentTypeId === 'graphicComposition') {
                return <GraphicComposition key={key} data={row} />
              }

              if (contentTypeId === 'stats') {
                return <Stats key={key} data={row} />
              }

              if (contentTypeId === 'hardCoded') {
                return <HardCoded key={key} data={row} />
              }

              if (contentTypeId === 'accordionGrid') {
                return <AccordionGrid key={key} data={row} />
              }
              if (contentTypeId === 'componentPagination') {
                return <Pagination key={key} data={row} />
              }

              return null
            })}
            {displayTimestamp ? <Timestamp data={timestamp} /> : null}
          </main>
        </Layout>
      </ApplyParamsContext.Provider>
    )
  }
}

CustomLanding.propTypes = {
  pageContext: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired
}

export default CustomLanding

/* eslint-disable react/prop-types */
export const Head = function ({ data }) {
  const {
    contentfulLandingPageGod: { seoReference }
  } = data
  return (
    seoReference?.schemaMarkup && (
      <script type="application/ld+json">
        {' '}
        {seoReference?.schemaMarkup?.schemaMarkup?.internal?.content}{' '}
      </script>
    )
  )
}
/* eslint-enable react/prop-types */

export const pageQuery = graphql`
  query ($contentful_id: String!) {
    contentfulSeoMetadata {
      customersHelped
      pageTitle
      dynamicApplyParams
    }
    contentfulLandingPageGod(contentful_id: { eq: $contentful_id }) {
      pageTitle
      seoReference {
        ...CustomSEOFragment
      }
      pricingStrategy
      slug
      extraApplyParams {
        internal {
          content
        }
      }
      hideNavigation
      navigation {
        ... on ContentfulNavbar {
          logo {
            imageAltText
            image {
              file {
                url
              }
            }
            url
          }
          mobileLogo {
            imageAltText
            image {
              file {
                url
              }
            }
            url
          }
          redeemOfferLinkGroup {
            title
            links {
              title
              url
              isExternalLink
              segmentEvent
              payload {
                internal {
                  content
                }
              }
            }
            internal {
              type
            }
          }
          loginButton {
            dataEvent
            internal {
              type
            }
            title
            url
            isExternalLink
          }
          myAccount {
            links {
              title
              url
              dataEvent
              isExternalLink
            }
            title
            internal {
              type
            }
          }
          blogCategoryNavigation {
            ... on ContentfulTextLink {
              title
              url
              internal {
                type
              }
            }
          }
          linkGroups {
            ... on ContentfulAvantComFooterNavList {
              title
              url
              links {
                title
                url
                isExternalLink
              }
              internal {
                type
              }
            }
            ... on ContentfulTextLink {
              title
              url
              internal {
                type
              }
            }
          }
        }
      }
      hero {
        ... on ContentfulCustomHero {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          bannerAlert
          size
          headline
          navItems {
            ... on ContentfulButton {
              action
              dataEvent
              hasEmailIcon
              openNewWindow
              removeArrowIcon
              textToLowercase
              pageCategory
              style
              text
              url
              contentfulAsset {
                file {
                  url
                }
              }
            }
          }
          headlineLogos {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
          backgroundMedia {
            ... on ContentfulImageWithText {
              id
              altText
              image {
                file {
                  url
                  fileName
                  contentType
                }
                gatsbyImageData
              }
            }
          }
          backgroundImageAlignment
          image {
            altText
            image {
              file {
                contentType
                fileName
                url
                details {
                  image {
                    height
                    width
                  }
                }
              }
              gatsbyImageData
            }
          }
          imageCard {
            ... on ContentfulInfoCard {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              description
              headline
            }
          }
          theme
          decoratives
          buttonColor
          contentAlign
          subHeadline
          hasPromoCodeBox
          buttons {
            ... on ContentfulHardCoded {
              __typename
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              option
            }
            ... on ContentfulButton {
              __typename
              action
              dataEvent
              hasEmailIcon
              openNewWindow
              removeArrowIcon
              textToLowercase
              pageCategory
              segmentEvent
              payload {
                internal {
                  content
                }
              }
              style
              text
              url
              contentfulAsset {
                file {
                  url
                }
              }
            }
          }
          beforeText
          showButtonArrow
          afterText
          cards {
            ... on ContentfulBlockCard {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              theme
              backgroundImage {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              backgroundImageMobile {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              images {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              imageMobile {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              activeLeadershipDesign
              mobileImageLocation
              hideImageMobile
              padImage
              overlay
              alignTextContent
              alignTextContentMobile
              headline
              subHeadline
              buttons {
                action
                dataEvent
                hasEmailIcon
                removeArrowIcon
                textToLowercase
                openNewWindow
                pageCategory
                style
                text
                url
                contentfulAsset {
                  file {
                    url
                  }
                }
              }
              afterText
              disclaimerText {
                disclaimerText
              }
              subText {
                subText
              }
              contactHoursText {
                raw
              }
            }
          }
          contents {
            ... on ContentfulImageSet {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              headline
              backgroundStyle
              images {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
            }
            ... on ContentfulChooseUsSet {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              entryTitle
              title
              theme
              isThreeColumns
              reasons {
                title
                logo {
                  altText
                  image {
                    description
                    file {
                      contentType
                      fileName
                      url
                    }
                    gatsbyImageData
                  }
                }
                text
                learnMoreUrl {
                  title
                  url
                }
              }
            }
            ... on ContentfulRatesAndTermsRibbon {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              pairs {
                first
                second
              }
              disclaimer {
                first
                second
              }
            }
          }
          disclaimer {
            disclaimer
          }
          activeNotFoundDesign
        }
      }
      rows {
        ... on ContentfulPartnerVisitThankYou {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          entryTitle
          partnerLogo {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
        }
        ... on ContentfulSchumerBox {
          schumerID
          cardPricingStrategyData {
            isFixedApr
            fixedApr
            fee
            amountId
            marginRate
            returnedPaymentFee
            overlimitFee
            showAnnualFeeBox
            annualMembershipFee
            annualMembershipFeeYearTwo
            monthlyMembershipFee
            monthlyMembershipFeeYearTwo
            isDailyBalance
            annualFeeNotice {
              raw
            }
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
          }
          sys {
            contentType {
              sys {
                id
              }
            }
          }
        }
        ... on ContentfulGraphicComposition {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          alignImage
          image {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
          text {
            text
          }
        }
        ... on ContentfulChooseUsSet {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          entryTitle
          title
          theme
          isThreeColumns
          reasons {
            title
            logo {
              altText
              image {
                description
                file {
                  contentType
                  fileName
                  url
                }
                gatsbyImageData
              }
            }
            text
            learnMoreUrl {
              title
              url
            }
          }
        }
        ... on ContentfulCarousel {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          title
          backgroundColor
          carouselItems {
            ... on ContentfulCarouselItem {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              fullName
              position
              blurb
              profilePicture {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
            }
            ... on ContentfulBlockCard {
              id
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              theme
              backgroundImage {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              backgroundImageMobile {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              images {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              imageMobile {
                altText
                image {
                  description
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              mobileImageLocation
              hideImageMobile
              padImage
              overlay
              alignTextContent
              alignTextContentMobile
              headline
              subHeadline
              buttons {
                action
                dataEvent
                hasEmailIcon
                openNewWindow
                removeArrowIcon
                textToLowercase
                pageCategory
                segmentEvent
                payload {
                  internal {
                    content
                  }
                }
                style
                text
                url
                contentfulAsset {
                  file {
                    url
                  }
                }
              }
              afterText
              disclaimerText {
                disclaimerText
              }
              subText {
                subText
              }
              contactHoursText {
                raw
              }
            }
          }
        }
        ... on ContentfulBlockCardGrid {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          backgroundImageGrid {
            altText
            image {
              file {
                url
                contentType
                fileName
              }
              gatsbyImageData
            }
          }
          grid
          showcaseCircles
          activeLeadershipDesign
          displayInMobile
          carouselArrowsColor
          showContentAsCarousel
          subtitle
          headline
          title
          alignText
          isBlog
          quote
          theme
          alignText
          ctaButton {
            dataEvent
            hasEmailIcon
            openNewWindow
            url
            style
            removeArrowIcon
            textToLowercase
            text
            action
            pageCategory
            segmentEvent
            payload {
              internal {
                content
              }
            }
            contentfulAsset {
              file {
                url
              }
            }
          }
          blockCards {
            ... on ContentfulTestingBlogPost {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              postTitle
              headline
              author
              slug
              publishDate
              postBody {
                raw
              }
              featuredImage {
                altText
                image {
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              tags
              categories
            }
            ... on ContentfulBlockCard {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              images {
                altText
                image {
                  description
                  file {
                    url
                    contentType
                  }
                  gatsbyImageData
                }
              }
              imageMobile {
                altText
                image {
                  description
                  file {
                    url
                    contentType
                    fileName
                  }
                  gatsbyImageData
                }
              }
              link {
                title
                url
              }
              backgroundImage {
                altText
                image {
                  description
                  file {
                    url
                    contentType
                    fileName
                  }
                  gatsbyImageData
                }
              }
              backgroundImageMobile {
                altText
                image {
                  description
                  file {
                    url
                    contentType
                    fileName
                  }
                  gatsbyImageData
                }
              }
              decorationBlockCard {
                altText
                image {
                  description
                  file {
                    url
                    contentType
                    fileName
                  }
                  gatsbyImageData
                }
              }
              theme
              hideImageMobile
              mobileImageLocation
              blockCardBox
              isBlog
              padImage
              overlay
              alignTextContent
              alignTextContentMobile
              headline
              subHeadline
              subText {
                subText
              }
              contactHoursText {
                raw
              }
              buttons {
                action
                dataEvent
                hasEmailIcon
                removeArrowIcon
                textToLowercase
                openNewWindow
                style
                text
                url
                segmentEvent
                payload {
                  internal {
                    content
                  }
                }
              }
            }
          }
        }
        ... on ContentfulRatesAndTermsRibbon {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          pairs {
            first
            second
          }
          disclaimer {
            first
            second
          }
        }
        ... on ContentfulTrustPilot {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          dataTags
          layout
          gridHeight
          button {
            id
            entryTitle
            dataEvent
            style
            hasEmailIcon
            url
            text
            action
            openNewWindow
            pageCategory
            contentfulAsset {
              file {
                url
              }
            }
          }
          trustPilotHeadline: headline {
            preHeaderText
            headerText
            backgroundColor
            textAlignment
            subtext {
              subtext
            }
          }
        }
        ... on ContentfulColumns {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
        }
        ... on ContentfulRatesTermsStatesDropdown {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          isSecuredLoans
          ratesTermsData {
            ratesAndTermsData {
              states {
                state_name
                letter_name
                active
                products {
                  apr_range
                  loan_amount_range
                  loan_term_range
                  disclaimers
                }
              }
            }
          }
        }
        ... on ContentfulCustomersHelped {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          headline
          afterText
        }
        ... on ContentfulBlockCard {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          theme
          backgroundImage {
            altText
            image {
              description
              title
              gatsbyImageData
              file {
                url
                contentType
              }
            }
          }
          backgroundImageMobile {
            altText
            image {
              description
              title
              gatsbyImageData
              file {
                url
                contentType
              }
            }
          }
          images {
            altText
            image {
              description
              title
              gatsbyImageData
              file {
                url
                contentType
              }
            }
          }
          imageMobile {
            altText
            image {
              description
              title
              gatsbyImageData
              file {
                url
                contentType
              }
            }
          }
          mobileImageLocation
          activeLeadershipDesign
          hideImageMobile
          padImage
          overlay
          alignTextContent
          alignTextContentMobile
          headline
          subHeadline
          buttons {
            action
            dataEvent
            hasEmailIcon
            openNewWindow
            pageCategory
            segmentEvent
            payload {
              internal {
                content
              }
            }
            style
            text
            url
            contentfulAsset {
              file {
                url
              }
            }
          }
          afterText
          disclaimerText {
            disclaimerText
          }
          subText {
            subText
          }
          contactHoursText {
            raw
          }
          activeLetsTalkDesign
        }
        ... on ContentfulInfoCard {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          theme
          contentAlign
          description
          textTable
          finePrintText
          headline
          rolloverImages {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
          image {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
          imageMobile {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
          imageButtons {
            ... on ContentfulButton {
              __typename
              id
              dataEvent
              entryTitle
              hasEmailIcon
              action
              style
              openNewWindow
              pageCategory
              segmentEvent
              payload {
                internal {
                  content
                }
              }
              removeArrowIcon
              spaceId
              text
              textToLowercase
              url
            }
            ... on ContentfulClickableImage {
              __typename
              id
              url
              imageAltText
              openNewWindow
              dataEvent
              image {
                gatsbyImageData(layout: CONSTRAINED, height: 58)
                file {
                  contentType
                  url
                }
              }
            }
          }
          text {
            text
          }
          blogLayout
          blogPost {
            ... on ContentfulTestingBlogPost {
              postTitle
              headline
              categories
              author
              publishDate(formatString: "MMMM DD, YYYY")
              tags
              slug
              postBody {
                raw
              }
              featuredImage {
                altText
                image {
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
            }
          }
        }
        ... on ContentfulImageSet {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          headline
          backgroundStyle
          images {
            altText
            image {
              description
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
        }
        ... on ContentfulHeadline {
          ...HeadlineFragment
        }
        ... on ContentfulPressLinksSet {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          headline
          backgroundColor
          pressLinks {
            title
            headerImage {
              altText
              image {
                file {
                  contentType
                  fileName
                  url
                }
                gatsbyImageData
              }
            }
            headerColor
            description {
              description
            }
            linkUrl
            linkAsset {
              file {
                url
              }
            }
            newDate
            date
            button {
              action
              removeArrowIcon
              textToLowercase
              dataEvent
              hasEmailIcon
              openNewWindow
              pageCategory
              style
              text
              url
              contentfulAsset {
                file {
                  url
                }
              }
            }
          }
        }
        ... on ContentfulTextBlock {
          ...RichTextBlockFragment
        }
        ... on ContentfulMemberFdicBlock {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          extras
          id
        }
        ... on ContentfulTestingBlogPost {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          postTitle
          headline
          author
          slug
          publishDate(formatString: "MMMM DD, YYYY")
          postBody {
            raw
            references {
              __typename
              contentful_id
              gatsbyImageData
              description
              title
            }
          }
          featuredImage {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
          tags
          categories
          cta {
            ... on ContentfulButton {
              action
              dataEvent
              hasEmailIcon
              removeArrowIcon
              textToLowercase
              openNewWindow
              pageCategory
              style
              text
              url
              contentfulAsset {
                file {
                  url
                }
              }
            }
          }
        }
        ... on ContentfulColumnsV3 {
          ...ContentfulColumnV3Fragment
        }
        ... on ContentfulDecorationV3 {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          shape
          color
          alignment
          appearsOnMobile
        }
        ... on ContentfulStats {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          style
          topHeadline {
            ... on ContentfulHeadline {
              ...HeadlineFragment
            }
          }
          statRows {
            numbers
            numbersTextSize
            text
            rangingFrom
            backgroundColor
          }
          legalStatement {
            statement {
              raw
            }
          }
        }
        ... on ContentfulHardCoded {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          option
        }
        ... on ContentfulComponentPagination {
          id
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          entryTitle
          itemsPerPage
          grid
          items {
            ... on ContentfulTestingBlogPost {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              postTitle
              headline
              author
              slug
              publishDate
              postBody {
                raw
              }
              featuredImage {
                altText
                image {
                  file {
                    contentType
                    fileName
                    url
                  }
                  gatsbyImageData
                }
              }
              tags
              categories
            }
          }
        }
      }
      legalStatements {
        ... on ContentfulLegalStatement {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          entryTitle
          styleFeatures
          statement {
            raw
          }
        }
        ... on ContentfulTextBlock {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          paragraph {
            raw
          }
        }
      }
      v3Styles
      disclosures {
        ... on ContentfulDisclosures {
          legalStatements {
            ... on ContentfulLegalStatement {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              entryTitle
              styleFeatures
              statement {
                raw
              }
            }
            ... on ContentfulTextBlock {
              sys {
                contentType {
                  sys {
                    id
                  }
                }
              }
              paragraph {
                raw
              }
            }
          }
          logos {
            altText
            image {
              file {
                contentType
                fileName
                url
              }
              gatsbyImageData
            }
          }
        }
      }
      footer {
        logo {
          url
          image {
            title
            file {
              url
            }
          }
        }
        linkGroups {
          title
          links {
            ... on ContentfulTextLink {
              title
              url
              payload {
                internal {
                  content
                }
              }
            }
          }
        }
        copyright
        socialIcons {
          url
          image {
            title
            file {
              url
            }
          }
        }
      }
    }
  }
`
